import {
  IWidgetConfigurationProps,
  withWidgetConfiguration,
} from "@sgwt-widget/core";
import { find } from "lodash";
import { h } from "preact";
import { ApiBackedScComponent } from "../../common/components/ApiBackedScComponent";
import { TreeMapOpt } from "../../common/components/TreeMapOpt";
import {
  MakeGenericApiCall,
  MakeSalesCreditRoleProfile,
} from "../../common/generic/generic-api";
import {
  CheckReponseDataForError,
  GenericFilterBuilder,
} from "../../common/generic/generic-helper";
import { SCAttributes } from "../../common/generic/generic-sc-attributes";
import { SCKpis } from "../../common/generic/generic-sc-domain";
import { IGenericRequest } from "../../common/generic/generic-types";
import { ConvertFilter } from "../../common/salescredit/salescredit-filter-help";
import { SCProfileDictionary } from "../../common/salescredit/salescredit-profile-dictionary";
import {
  BuildSalesCreditFilters,
  CreateDateFromFilter,
} from "../../common/salescredit/salescredit-search-criteria";
import { IProps } from "../../common/salescredit/salescredit-types";
import { maxSelectedClients } from "../../common/utils/clientlistHelper";
import { fromStyleLocals } from "../../common/utils/css";
import { ICprSalesCreditSearchCriteria } from "../../common/utils/types";
import { BuildSalesBySectorTreeMap, mapData } from "../utils/helper";

// tslint:disable-next-line:no-var-requires
const style = require("./SalesCreditBySales.less");
const css = fromStyleLocals(style.locals);

interface IState {
  criteria: ICprSalesCreditSearchCriteria;
  email: string;
  options: any;
}

export class SalesCreditSalesBySectorClass extends ApiBackedScComponent<
  IProps & IWidgetConfigurationProps,
  IState
> {
  private readonly LegendStyle = {
    label: {
      paddingLeft: "5px",
      paddingRight: "15px",
    },
    minus1: {
      color: "#f8dad9",
    },
    minus2: {
      color: "#f1b5b3",
    },
    minus3: {
      color: "#ea918d",
    },
    minus4: {
      color: "#e36c67",
    },
    minus5: {
      color: "#dc4941",
    },
    plus1: {
      color: "#d9f3eb",
    },
    plus2: {
      color: "#b3e7d7",
    },
    plus3: {
      color: "#8ddbc4",
    },
    plus4: {
      color: "#67cfb0",
    },
    plus5: {
      color: "#41c49d",
    },
  };

  private readonly grapDivStyle = {
    boxSizing: "border-box",
    height: "calc(100% - 50px)",
    width: "100%",
  };
  constructor() {
    super();
    this.setState({
      criteria: {},
      email: "",
      options: undefined,
    });
  }
  public renderWithDataReady(): JSX.Element {
    return (
      <div
        id="container"
        style="width: 100%; height: 100%; boxSizing: border-box"
      >
        <div id="container" style={this.grapDivStyle}>
          <TreeMapOpt options={this.state.options} />
        </div>
        <div style="text-align: center; height: 25px">
          <i class="fa fa-square-o" />
          &nbsp;&nbsp;&nbsp;
          <span>Size shows the % of total YTD</span>
        </div>
        <div style="text-align: center; height: 25px">
          <span>-100%</span>&nbsp;&nbsp;&nbsp;
          <i
            class="fa fa-square"
            id="minusfive"
            style={this.LegendStyle.minus5}
            aria-hidden="true"
          />
          <i
            class="fa fa-square"
            id="minusfour"
            style={this.LegendStyle.minus4}
            aria-hidden="true"
          />
          <i
            class="fa fa-square"
            id="minusthree"
            style={this.LegendStyle.minus3}
            aria-hidden="true"
          />
          <i
            class="fa fa-square"
            id="minustwo"
            style={this.LegendStyle.minus2}
            aria-hidden="true"
          />
          <i
            class="fa fa-square"
            id="minusone"
            style={this.LegendStyle.minus1}
            aria-hidden="true"
          />
          <i
            class="fa fa-square"
            id="one"
            style={this.LegendStyle.plus1}
            aria-hidden="true"
          />
          <i
            class="fa fa-square"
            id="two"
            style={this.LegendStyle.plus2}
            aria-hidden="true"
          />
          <i
            class="fa fa-square"
            id="three"
            style={this.LegendStyle.plus3}
            aria-hidden="true"
          />
          <i
            class="fa fa-square"
            id="four"
            style={this.LegendStyle.plus4}
            aria-hidden="true"
          />
          <i
            class="fa fa-square"
            id="five"
            style={this.LegendStyle.plus5}
            aria-hidden="true"
          />
          &nbsp;&nbsp;&nbsp;<span>+100%</span>&nbsp;&nbsp;&nbsp;<span>YoY</span>
        </div>
      </div>
    );
  }
  protected shouldFetchApiData(nextProps: IProps): boolean {
    return true;
  }

  protected async fetchApiData(props: IProps) {
    this.initCorrelationId();
    this.setDataStatus("loading");
    const headers = props.headers;
    const userRole = await MakeSalesCreditRoleProfile(
      props.headers,
      this.props.widgetConfiguration,
      undefined,
      this.state.correlationId
    );

    if (userRole.Role === undefined) {
      this.setDataStatus("no-access-rights");
      this.setStatusText(userRole.StatusText);
      return;
    }

    const filtersConverted = ConvertFilter(props.filters);
    const filters = BuildSalesCreditFilters(filtersConverted);
    const profileDictionary = new SCProfileDictionary();
    const kpis = [
      SCKpis.SC_YTD,
      SCKpis.SC_YTD_1,
      SCKpis.SC_YOY_PER,
      SCKpis.SC_SC_1,
      SCKpis.SC_Y_VS_Y1,
      SCKpis.SC_YTD_2,
      SCKpis.SC_YTD_3,
    ].map(x => profileDictionary.GetKpi(userRole.Profile, x));

    const genericRequest: IGenericRequest = {
      aggregationAttributes: [
        SCAttributes.SC_CLIENT_LE_TYPEL2_NAME,
        SCAttributes.SC_CLIENT_LE_TYPEL3_NAME,
      ].join(","),
      date: CreateDateFromFilter(filtersConverted),
      filter: GenericFilterBuilder(filters),
      kpis: kpis.join(","),
      sort: "-" + profileDictionary.GetKpi(userRole.Profile, SCKpis.SC_YTD),
    };
    const filterClientsSelected = find(filters, {
      attribute: "clientgen_id",
    });
    const clientsCount = filterClientsSelected
      ? filterClientsSelected.value.split("+").length
      : 0;
    if (filterClientsSelected && clientsCount >= maxSelectedClients) {
      this.setState({
        options: undefined,
      });
      this.setDataStatus("too-many-clients");
    } else {
      const responseData = await MakeGenericApiCall(
        headers,
        "sc",
        genericRequest,
        this.props.widgetConfiguration,
        this.state.correlationId
      );
      const errorStatus = CheckReponseDataForError(responseData);
      if (errorStatus) {
        this.setDataStatus(errorStatus);
        this.setStatusText(responseData.statusText);
        this.setState({
          options: undefined,
        });
        return;
      }
      const mappedData = mapData(responseData, userRole);

      if (!mappedData.ValidData) {
        this.setState({
          options: undefined,
        });
        this.setDataStatus("no-data");
        return;
      }

      const treeOptions = BuildSalesBySectorTreeMap(mappedData);

      if (responseData !== undefined) {
        this.setState({
          criteria: genericRequest,
          options: treeOptions,
        });
        this.setDataStatus("ready");
      } else {
        this.setState({
          criteria: {},
          options: undefined,
        });
        this.setDataStatus("no-data");
      }
    }
  }
}

export const SalesCreditSalesBySector = withWidgetConfiguration(
  SalesCreditSalesBySectorClass
);
